<script setup>
import {defineProps, ref, watch, computed} from "vue";
import {useQuery} from "@vue/apollo-composable";
import {GET_TOURNAMENT_PLAYERS} from "@/graphql/operations/tournament.ts";
import {GET_CATEGORIES} from "@/graphql/operations/categories.ts";

// Props
const props = defineProps({
  tournament: {
    type: Object,
    default: null,
  },
});

// State
const keyword = ref("");
const selectedCategory = ref("");
const pagination = ref({
  current: 1,
  pageSize: 10,
  total: 0,
  showSizeChanger: true,
});

const totalPages = computed(() =>
    Math.ceil(pagination.value.total / pagination.value.pageSize)
);

// Filters
const filters = computed(() => {
  const filters = {
    tournament: {documentId: {eq: props.tournament?.documentId}},
    categories: { documentId: { contains: selectedCategory.value } },
  };
  if (keyword.value) {
    filters.player = {display_name: {contains: keyword.value}};
  }
  return filters;
});

// Query
const {result, refetch} = useQuery(
    GET_TOURNAMENT_PLAYERS,
    () => ({
      page: parseInt(pagination.value.current),
      pageSize: parseInt(pagination.value.pageSize),
      filters: filters.value,
    }),
    {
      fetchPolicy: "no-cache",
    }
);

// Data handling
const tourPlayers = computed(() => result.value?.tourPlayers_connection?.nodes || []);

watch(result, () => {
  const pageInfo = result.value?.tourPlayers_connection?.pageInfo;
  if (pageInfo) {
    pagination.value.total = pageInfo.total || 0;
    pagination.value.current = pageInfo.page || 1;
  }
});

// Pagination Controls
const handleTableChange = () => {
  pagination.value.current = 1;
};

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    pagination.value.current = page;
  }
};

// Keyword Search Handler
const searchKeyword = () => {
  handleTableChange();
};

const searchCategory = () => {
  refetch({
    page: parseInt(pagination.value.current),
    pageSize: parseInt(pagination.value.pageSize),
    filters: filters.value,
  });
};

// Watch prop change
watch(
    () => props.tournament,
    (newTournament) => {
      if (newTournament) {
        refetch();
      }
    }
);

const getTourPlayerStatusClass = (status) => {
  switch (status) {
    case 'accepted': {
      return 'badge bg-success';
    }
    case 'registered': {
      return 'badge bg-warning';
    }
    case 'rejected': {
      return 'badge bg-danger';
    }
    default: {
      return 'badge bg-info';
    }
  }
}

const getTourPlayerStatusText = (status) => {
  switch (status) {
    case 'accepted': {
      return 'Đã duyệt';
    }
    case 'registered': {
      return 'Đã đăng ký';
    }
    case 'rejected': {
      return 'Đã từ chối';
    }
    default: {
      return 'Chưa rõ';
    }
  }
}

const optionCategories = ref([]);

const { result: getCategories } = useQuery(
    GET_CATEGORIES,
    () => ({
      documentId: props.tournament?.documentId,
      page: 1,
      pageSize: 50,
    }),
    {
      fetchPolicy: 'network-only',
    },
);

watch(
    () => getCategories.value?.categories_connection,
    (newCategories) => {
      if (newCategories) {
        optionCategories.value = [{ label: 'Lựa chọn nội dung', value: '' }];
        const listCategories = newCategories.nodes;
        for (const category of listCategories) {
          optionCategories.value.push({
            value: category.documentId,
            label: category.name,
          });
        }
      } else {
        optionCategories.value = [{ label: 'Lựa chọn nội dung', value: '' }];
      }
    },
    { deep: true },
);
</script>

<template>
  <div class="coache-head-blk mb-3">
    <div class="d-flex justify-content-end align-items-center">
      <div class="w-100">
        <div class="d-flex w-100 justify-content-start gap-4">
          <div id="tablefilter">
            <div class="dataTables_filter d-flex gap-1">
              <label>
                <select style="min-width: unset" class="form-control" v-model="selectedCategory" @change="searchCategory">
                  <option v-for="item in optionCategories" :key="item.value" :value="item.value">
                    {{ item.label }}
                  </option>
                </select>
              </label>
              <label>
                <input style="min-width: unset" v-model="keyword" class="form-control form-control-sm" placeholder="Tìm kiếm" type="search">
              </label>
            </div>
          </div>
          <div class="request-coach-list">
            <div class="card-header-btns">
              <nav>
                <div class="nav nav-tabs" role="tablist">
                  <button class="nav-link active text-nowrap" type="button" @click="searchKeyword">
                    Tìm kiếm
                  </button>
                </div>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="table-responsive table-datatble">
    <table class="table table-borderless dashboard-card-table">
      <thead class="thead-light">
      <tr>
        <th>Họ và tên</th>
        <th>Nội dung</th>
        <th>Trạng thái</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="(item, index) in tourPlayers" :key="item.id" :data-key="index" class="text-start">
        <td>
          {{ item?.player?.display_name }}
        </td>
        <td>
          {{ item?.categories.map(category => category.name).join(', ') }}
        </td>
        <td>
          <p class="d-flex justify-content-start">
            <span :class="getTourPlayerStatusClass(item?.tour_player_status)">
              {{ getTourPlayerStatusText(item?.tour_player_status) }}
            </span>
          </p>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
  <div class="tab-footer">
    <div class="row">
      <div class="col-md-6">
        <div id="tablelength">
          <div class="dataTables_length">
            <label>Hiển thị
              <select
                  v-model="pagination.pageSize"
                  class="custom-select custom-select-sm form-control form-control-sm"
                  @change="handleTableChange"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="999999">All</option>
              </select>
            </label>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <div id="tablepage">
          <div class="dataTables_paginate paging_simple_numbers">
            <ul class="pagination">
              <li
                  :class="{ disabled: pagination.current === 1 }"
                  class="paginate_button page-item previous"
                  @click="changePage(pagination.current - 1)"
              >
                <a class="page-link" href="javascript:void(0);">
                  <i class="feather-chevrons-left"></i>
                </a>
              </li>

              <li
                  v-for="page in totalPages"
                  :key="page"
                  :class="{ active: pagination.current === page }"
                  class="paginate_button page-item"
                  @click="changePage(page)"
              >
                <a class="page-link" href="javascript:void(0);">{{ page }}</a>
              </li>

              <li
                  :class="{ disabled: pagination.current === totalPages }"
                  class="paginate_button page-item next"
                  @click="changePage(pagination.current + 1)"
              >
                <a class="page-link" href="javascript:void(0);">
                  <i class="feather-chevrons-right"></i>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>